// Puerto de packages/hce/src/hce/paciente/HistoriaTab.tsx — rediseñado
// 2026-09-27 (pedido explícito de Sue): esta pestaña ahora muestra SOLO los
// documentos guardados y firmados (cualquier familia: Questionnaire genérico,
// discretos, consentimientos, FEMO, recetas, y los que viven bajo las
// pestañas "Documentos"/"Laboratorio y Estudios" — todos crean el mismo
// DocumentReference con las extensiones de firma, ver lib/documentosFirmados.ts),
// con un botón "Ver/imprimir" que abre el PDF ya firmado en un visor. El
// resto de la cronología clínica (diagnósticos, recetas, signos vitales,
// atenciones) ya vive en la pestaña Resumen (FacesheetTab.tsx) — mostrarla acá
// también era pura duplicación. Se conserva la única utilidad que no existe
// en ningún otro lado: descartar un Encounter ambulatorio que quedó
// "in-progress" sin que nadie lo cerrara (2026-09-11, pedido explícito).
import { useMedplum } from '@medplum/react-hooks';
import type { DocumentReference, Encounter, Patient, Provenance, Reference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { esDocumentoFirmado, fechaFirmaDe, tituloDocumentoFirmado } from '@/lib/documentosFirmados';
import { especialidadUsaPoliticaMedico } from '@/lib/catalogoEspecialidades';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { puedeAccionProfesional, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { buscarAdendasDe } from '../lib/audit/bloqueo';
import { AdendaDialog } from './AdendaDialog';
import { AdendasDelDocumento } from './AdendasDelDocumento';
import { BotonCopiaHistoria } from './BotonCopiaHistoria';
import { TablaVacia } from './TablaVacia';
import { VisorDocumentoFirmado } from './VisorDocumentoFirmado';

function fechaLegible(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-EC');
  } catch {
    return iso;
  }
}

export function HistoriaTab(): JSX.Element {
  // BA-C-11: entregar la copia de la historia al paciente es de médico, odontólogo, psicólogo y otros profesionales de salud (el servidor rechaza al resto).
  const rolPracticante = useRolDelPracticante();
  const puedeEntregarCopia = puedeAccionProfesional(rolPracticante, 'documento-al-paciente');
  // H-015: solo la interfaz. Escribir Provenance solo lo permite la AccessPolicy «Médico» (odontólogo, psicólogo, enfermería y obstetriz la tienen de solo
  // lectura); /mi-rol no ofrece una acción para Provenance, así que se condiciona a la misma política que el servidor asigna por especialidad. Mientras se
  // desconoce la especialidad, no se muestra. El servidor decide siempre: un 403 se traduce en el diálogo.
  const especialidad = useEspecialidadDelPracticante();
  const puedeAdendar =
    puedeAccionProfesional(rolPracticante, 'firmar-adenda') && !especialidad.cargando && especialidad.codigo !== null && especialidadUsaPoliticaMedico(especialidad.codigo);
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [documentos, setDocumentos] = useState<DocumentReference[]>();
  const [encountersAbiertos, setEncountersAbiertos] = useState<Encounter[]>([]);
  const [cargando, setCargando] = useState(true);
  const [documentoAbierto, setDocumentoAbierto] = useState<DocumentReference | null>(null);
  const [adendasPorDoc, setAdendasPorDoc] = useState<Record<string, Provenance[]>>({});
  const [adendasTruncadas, setAdendasTruncadas] = useState(false);
  const [documentoAdenda, setDocumentoAdenda] = useState<DocumentReference | null>(null);
  const [descartandoEncounter, setDescartandoEncounter] = useState<string | null>(null);

  const cargar = useCallback(() => {
    let cancelado = false;
    setCargando(true);
    Promise.all([
      medplum.searchResources('DocumentReference', { patient: `Patient/${id}`, _count: 200 }),
      medplum.searchResources('Encounter', { patient: `Patient/${id}`, status: 'in-progress', class: 'AMB', _count: 50 }),
    ])
      .then(([docs, encounters]) => {
        if (cancelado) return;
        const firmados = (docs as DocumentReference[]).filter((d) => esDocumentoFirmado(d) && !d.relatesTo?.some((r) => r.code === 'appends')); // la adenda firmada no se lista aparte: queda como Provenance (AdendasDelDocumento, sin enlace al PDF); el PDF firmado se abre desde DocumentosTab
        setDocumentos(firmados);
        // Las adendas no bloquean la lista: UNA consulta por paciente (por lotes), agrupada por documento. Si falla, se muestran los documentos sin la marca.
        buscarAdendasDe(
          medplum,
          firmados.filter((d) => d.id).map((d) => `DocumentReference/${d.id}`),
        )
          .then((r) => {
            if (cancelado) return;
            setAdendasPorDoc(r.porDocumento);
            setAdendasTruncadas(r.truncada);
          })
          .catch(() => undefined);
        setEncountersAbiertos(encounters as Encounter[]);
        setCargando(false);
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
        setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, id]);

  useEffect(() => cargar(), [cargar]);

  async function descartarEncounter(encounterId: string): Promise<void> {
    // D-052: `cancelled` es terminal; el servidor ya no deja volver a abrir la atención (solo anularla). Se pide confirmación antes.
    if (!window.confirm(t('fichaPaciente.historia.confirmarDescartarAtencion'))) return;
    setDescartandoEncounter(encounterId);
    try {
      const encounter = await medplum.readResource('Encounter', encounterId);
      await medplum.updateResource<Encounter>({ ...encounter, status: 'cancelled' });
      cargar();
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescartandoEncounter(null);
    }
  }

  const subject = { reference: `Patient/${id}` } as Reference<Patient>;

  const lista = (documentos ?? []).slice().sort((a, b) => new Date(fechaFirmaDe(b) ?? 0).getTime() - new Date(fechaFirmaDe(a) ?? 0).getTime());

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.historia')}</h5>
        {puedeEntregarCopia && <BotonCopiaHistoria subject={subject} />}
      </div>

      {encountersAbiertos.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-orange-300 bg-orange-50 p-3">
          <h6 className="text-sm font-semibold">{t('fichaPaciente.historia.atencionesEnCurso')}</h6>
          <p className="text-xs text-hce-muted">{t('fichaPaciente.historia.atencionesEnCursoDetalle')}</p>
          <ul className="flex flex-col gap-1">
            {encountersAbiertos.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2 text-sm">
                <Link className="text-hce-pine underline-offset-2 hover:underline" to={`/${id}/formulario/001?encounter=${encodeURIComponent(`Encounter/${e.id}`)}`}>
                  {fechaLegible(e.period?.start)} — {t('fichaPaciente.historia.atencion')}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={descartandoEncounter === e.id}
                  onClick={() => void descartarEncounter(e.id!)}
                >
                  {descartandoEncounter === e.id ? t('fichaPaciente.descartando') : t('fichaPaciente.descartar')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.historia.sinDocumentosFirmados')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.formulario')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.acciones')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{fechaLegible(fechaFirmaDe(d))}</TableCell>
                <TableCell>{tituloDocumentoFirmado(d)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => setDocumentoAbierto(d)}>
                    {t('formulario.firma.verImprimir')}
                  </Button>
                  {puedeAdendar && (
                    <Button variant="ghost" size="sm" onClick={() => setDocumentoAdenda(d)}>
                      {t('fichaPaciente.historia.agregarAdenda')}
                    </Button>
                  )}
                  <AdendasDelDocumento adendas={(d.id && adendasPorDoc[`DocumentReference/${d.id}`]) || []} idPanel={`adendas-${d.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {adendasTruncadas && (
        <p role="status" className="text-xs text-amber-800">
          {t('fichaPaciente.adenda.truncada')}
        </p>
      )}
      <AdendaDialog
        documento={documentoAdenda}
        onCerrar={() => setDocumentoAdenda(null)}
        onCreada={() => {
          setDocumentoAdenda(null);
          notify({ color: 'green', message: t('fichaPaciente.adenda.creada') });
          cargar();
        }}
      />
      <VisorDocumentoFirmado documento={documentoAbierto} onCerrar={() => setDocumentoAbierto(null)} />
    </div>
  );
}
